import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Plus, Minus, Trash2, ShoppingBag, Store, CheckCircle2, Phone } from "lucide-react";
import { useCart } from "../context/CartContext";
import { BUSINESS, getPickupSlots } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CartDrawer() {
  const { items, open, setOpen, setQty, remove, clear, total } = useCart();
  const [step, setStep] = useState("cart");
  const [form, setForm] = useState({ name: "", phone: "", email: "", notes: "" });
  const [placing, setPlacing] = useState(false);
  const [orderNumber, setOrderNumber] = useState(null);
  const [error, setError] = useState("");
  const [pickup, setPickup] = useState("asap");
  const [slot, setSlot] = useState("");
  const [confirmedPickup, setConfirmedPickup] = useState("");
  const [confirmedEmail, setConfirmedEmail] = useState("");
  const slots = useMemo(() => getPickupSlots(), [open]);

  const close = () => {
    setOpen(false);
    if (step === "done") setTimeout(() => setStep("cart"), 400);
  };

  const placeOrder = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      setError("Please enter your name and phone number.");
      return;
    }
    const pickupTime = pickup === "later" && slots.length > 0 ? slot || slots[0] : "ASAP";
    setPlacing(true);
    setError("");
    try {
      const res = await fetch(`${API}/orders`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          customer_name: form.name.trim(),
          phone: form.phone.trim(),
          customer_email: form.email.trim(),
          pickup_time: pickupTime,
          notes: form.notes.trim(),
          items: items.map((i) => ({ name: i.name, price: parseFloat(i.price), qty: i.qty, selections: i.selections || "" })),
          total,
        }),
      });
      if (!res.ok) throw new Error("order failed");
      const data = await res.json();
      setOrderNumber(data.order_number);
      setConfirmedPickup(pickupTime);
      setConfirmedEmail(form.email.trim());
      setStep("done");
      clear();
    } catch {
      setError(`Couldn't send your order. Please call us on ${BUSINESS.phoneDisplay} instead.`);
    } finally {
      setPlacing(false);
    }
  };

  const inputCls =
    "w-full border border-charcoal/20 bg-white/60 px-4 py-3 text-sm text-charcoal placeholder:text-charcoal/40 focus:border-ember focus:outline-none";

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            data-testid="cart-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={close}
            className="fixed inset-0 z-[95] bg-charcoal/50 backdrop-blur-sm"
          />
          <motion.aside
            data-testid="cart-drawer"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-y-0 right-0 z-[96] flex w-full max-w-md flex-col bg-cream shadow-2xl"
          >
            <div className="flex items-center justify-between border-b border-black/10 px-6 py-5">
              <p className="flex items-center gap-3 font-display text-xl uppercase tracking-wide">
                <ShoppingBag size={20} className="text-ember" /> Your Order
              </p>
              <button data-testid="cart-close-button" onClick={close} aria-label="Close cart" className="text-charcoal/60 transition-colors hover:text-ember">
                <X size={24} />
              </button>
            </div>

            {step === "done" ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-5 px-8 text-center" data-testid="order-confirmation">
                <CheckCircle2 size={56} className="text-ember" />
                <p className="font-display text-3xl uppercase tracking-wide">Order Received!</p>
                <p className="text-sm text-charcoal/60">
                  Show this order number when you arrive:
                </p>
                <p data-testid="order-number" className="border-2 border-dashed border-ember px-8 py-4 font-display text-4xl tracking-[0.2em] text-ember">
                  {orderNumber}
                </p>
                <p data-testid="order-pickup-time" className="text-sm text-charcoal/70">
                  Pickup: <strong>{confirmedPickup}</strong>
                </p>
                <p className="flex items-center gap-2 text-sm font-semibold text-charcoal">
                  <Store size={16} className="text-ember" /> Pay in store when you pick up
                </p>
                {confirmedEmail && (
                  <p data-testid="order-email-note" className="text-xs text-charcoal/50">
                    A copy of your order was sent to {confirmedEmail}
                  </p>
                )}
                <p className="text-xs text-charcoal/50">
                  {BUSINESS.address} · Questions? Call {BUSINESS.phoneDisplay}
                </p>
                <button
                  data-testid="order-done-button"
                  onClick={close}
                  className="mt-4 rounded-full bg-ember px-8 py-3.5 text-xs font-bold uppercase tracking-widest text-cream transition-transform duration-300 hover:scale-105"
                >
                  Done
                </button>
              </div>
            ) : items.length === 0 ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-4 px-8 text-center" data-testid="cart-empty">
                <ShoppingBag size={48} className="text-charcoal/20" />
                <p className="font-display text-2xl uppercase tracking-wide text-charcoal/60">Your cart is empty</p>
                <p className="text-sm text-charcoal/50">Add some pizzas, burgers or sides to get started.</p>
                <button
                  data-testid="cart-browse-menu-button"
                  onClick={close}
                  className="mt-2 rounded-full bg-ember px-8 py-3.5 text-xs font-bold uppercase tracking-widest text-cream transition-transform duration-300 hover:scale-105"
                >
                  Browse Menu
                </button>
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto px-6 py-4">
                  <ul className="divide-y divide-black/10">
                    {items.map((i) => (
                      <motion.li
                        key={i.id}
                        layout
                        initial={{ opacity: 0, x: 40 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                        data-testid={`cart-item-${i.id}`}
                        className="flex items-center gap-4 py-4"
                      >
                        <div className="flex-1">
                          <p className="font-display text-base uppercase tracking-wide">{i.name}</p>
                          <p className="text-xs text-charcoal/50">${i.price} each</p>
                          {i.selections && (
                            <p data-testid={`cart-selections-${i.id}`} className="mt-1 text-[0.7rem] font-medium leading-snug text-ember/80">
                              {i.selections}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            data-testid={`cart-decrease-${i.id}`}
                            onClick={() => setQty(i.id, i.qty - 1)}
                            aria-label={`Decrease ${i.name}`}
                            className="flex h-8 w-8 items-center justify-center rounded-full border border-charcoal/20 transition-colors hover:border-ember hover:text-ember"
                          >
                            <Minus size={13} />
                          </button>
                          <span data-testid={`cart-qty-${i.id}`} className="w-6 text-center text-sm font-bold">{i.qty}</span>
                          <button
                            data-testid={`cart-increase-${i.id}`}
                            onClick={() => setQty(i.id, i.qty + 1)}
                            aria-label={`Increase ${i.name}`}
                            className="flex h-8 w-8 items-center justify-center rounded-full border border-charcoal/20 transition-colors hover:border-ember hover:text-ember"
                          >
                            <Plus size={13} />
                          </button>
                        </div>
                        <p className="w-16 text-right font-display text-base text-ember">${(i.qty * parseFloat(i.price)).toFixed(2)}</p>
                        <button data-testid={`cart-remove-${i.id}`} onClick={() => remove(i.id)} aria-label={`Remove ${i.name}`} className="text-charcoal/40 transition-colors hover:text-ember">
                          <Trash2 size={16} />
                        </button>
                      </motion.li>
                    ))}
                  </ul>
                </div>

                <form onSubmit={placeOrder} className="border-t border-black/10 bg-white/40 px-6 py-5">
                  <div className="mb-4 flex items-baseline justify-between">
                    <span className="text-xs font-bold uppercase tracking-[0.25em] text-charcoal/50">Total</span>
                    <span data-testid="cart-total" className="font-display text-2xl text-ember">${total.toFixed(2)}</span>
                  </div>
                  <p className="mb-3 text-xs font-bold uppercase tracking-[0.25em] text-charcoal/50">Your Details</p>
                  <div className="space-y-3">
                    <input
                      data-testid="checkout-name-input"
                      className={inputCls}
                      placeholder="Your name *"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                    />
                    <input
                      data-testid="checkout-phone-input"
                      className={inputCls}
                      placeholder="Phone number *"
                      type="tel"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    />
                    <input
                      data-testid="checkout-email-input"
                      className={inputCls}
                      placeholder="Email (optional — get a copy of your order)"
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                    />
                    <input
                      data-testid="checkout-notes-input"
                      className={inputCls}
                      placeholder="Notes (optional) — e.g. no onion"
                      value={form.notes}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    />
                  </div>
                  <p className="mt-4 mb-3 text-xs font-bold uppercase tracking-[0.25em] text-charcoal/50">Pickup Time</p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      data-testid="pickup-asap-button"
                      onClick={() => setPickup("asap")}
                      className={`flex-1 rounded-full border px-4 py-3 text-xs font-bold uppercase tracking-widest transition-all duration-300 ${
                        pickup === "asap" ? "border-ember bg-ember text-cream" : "border-charcoal/20 text-charcoal/60 hover:border-ember hover:text-ember"
                      }`}
                    >
                      ASAP
                    </button>
                    <button
                      type="button"
                      data-testid="pickup-later-button"
                      onClick={() => setPickup("later")}
                      disabled={slots.length === 0}
                      className={`flex-1 rounded-full border px-4 py-3 text-xs font-bold uppercase tracking-widest transition-all duration-300 disabled:opacity-40 ${
                        pickup === "later" ? "border-ember bg-ember text-cream" : "border-charcoal/20 text-charcoal/60 hover:border-ember hover:text-ember"
                      }`}
                    >
                      Later Today
                    </button>
                  </div>
                  {pickup === "later" && slots.length > 0 && (
                    <select
                      data-testid="pickup-time-select"
                      className={`${inputCls} mt-3`}
                      value={slot || slots[0]}
                      onChange={(e) => setSlot(e.target.value)}
                    >
                      {slots.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  )}
                  <p data-testid="checkout-allergy-note" className="mt-4 border border-dashed border-charcoal/25 bg-white/50 px-3 py-2 text-[0.7rem] leading-snug text-charcoal/60">
                    Allergies? Let us know in the notes above or call {BUSINESS.phoneDisplay} — our kitchen handles gluten, dairy &amp; other allergens.
                  </p>
                  {error && (
                    <p data-testid="checkout-error" className="mt-3 text-xs font-semibold text-ember">{error}</p>
                  )}
                  <button
                    data-testid="place-order-button"
                    type="submit"
                    disabled={placing}
                    className="mt-4 flex w-full items-center justify-center gap-3 rounded-full bg-ember px-8 py-4 text-sm font-bold uppercase tracking-widest text-cream transition-all duration-300 hover:bg-[#991B1B] disabled:opacity-60"
                  >
                    <Store size={16} />
                    {placing ? "Sending Order..." : "Place Order — Pay In Store"}
                  </button>
                  <a
                    data-testid="cart-call-instead"
                    href={BUSINESS.phoneHref}
                    className="mt-3 flex items-center justify-center gap-2 text-xs font-semibold text-charcoal/50 transition-colors hover:text-ember"
                  >
                    <Phone size={12} /> Prefer to call? {BUSINESS.phoneDisplay}
                  </a>
                </form>
              </>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
